import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import axios from 'axios'
import TableComponent from '../../Components/CommonComponent/Table/Table';
import SaveButton from '../../Components/CommonComponent/Button/SaveButton';
import { FaEdit } from 'react-icons/fa';
import { RiDeleteBin6Fill } from 'react-icons/ri';
import { CommonContext } from '../../App';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import CancelButton from '../../Components/CommonComponent/Button/CancelButton';
import ContactMarquee from '../../Components/CommonComponent/Table/ContactMarquee';
import api from '../../services/api';
import LoadingSpinner from '../../Components/CommonComponent/loadingSpiner/LoadingSpinner';
import DeleteDataModal from '../../Components/CommonComponent/Modals/DeleteDataModal';

export default function ReceiptTable({ isModal = false, updatedDate, onModalClose, onModalSuccess, entry: modalEntry }) {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { entry: locationEntry, voucher_type, comeBack } = location.state || {};
  const { userData, branchId, dateRange } = useContext(CommonContext);

  const adminId = userData?.user?.id;
  const userRole = userData?.user?.role;
  const entry = isModal ? modalEntry : locationEntry;


  const [receiptData, setReceiptData] = useState([]);
  const [finalData, setFinalData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [resetKey, setResetKey] = useState(0);

  const [editingEntry, setEditingEntry] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEntryKey, setEditingEntryKey] = useState(null);

  const [allLedgers, setAllLedgers] = useState([]);
  const [ledgerData, setLedgerData] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [allSubGroups, setAllSubGroups] = useState([]);
  const [allPaymentData, setAllPaymentData] = useState([]);

  const [totalRecoard, setTotalRecoard] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  /* ---- Proper loading states ---- */
  const [pageLoading, setPageLoading] = useState(false);     
  const [scrollLoading, setScrollLoading] = useState(false); 
  const [saving, setSaving] = useState(false);               

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const LIMIT = 15;
  const [endDate, setEndDate] = useState(dateRange.endDate);

  /* ---------------------------- EFFECTS ---------------------------- */

  useEffect(() => {
    setEndDate(dateRange.endDate);
    // console.log(dateRange)
  }, [dateRange.endDate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const firstInput = document.querySelector('input[type="text"]');
      if (firstInput) firstInput.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!entry) return;
    setEditingEntry(entry);
      if(isModal){
      setIsEditMode(true);
    }
    setEditingEntryKey(entry.receiptKey);
    setReceiptData(entry.receipts || []);
    setResetKey(prev => prev + 1);
  }, [entry]);

  /* ---------------------------- API CALLS ---------------------------- */

  const fetchLedgerData = useCallback(async () => {
    if (!branchId) return;
    try {
      const res = await api.get(`ledgers/get-all-ledger?branchId=${branchId}&isActive=1`);
      if (res.data.success) {
        setAllLedgers(res.data.allData || []);
        setLedgerData(res.data.allData || []);
      }
    } catch (err) {
      toast.error(err.message || "Failed to fetch ledgers");
    }
  }, [branchId]);

  const fetchPaymentAccount = useCallback(async () => {
    if (!branchId) return;
    try {
      const res = await api.get(`payment-structure/get-all-data?branchId=${branchId}`);
      if (res.data.success) {
        setAllPaymentData(res.data.data || []);
      }
    } catch (err) {
      toast.error(err.message || "Failed to fetch payment accounts");
    }
  }, [branchId]);

  const fetchGroups = useCallback(async () => {
    if (!branchId) return;
    try {
      const res = await api.get(`groups-types/get-all-group?branchId=${branchId}`);
      if (res.data.success) setAllGroups(res.data.allData || []);
    } catch {
      toast.error("Failed to fetch groups");
    }
  }, [branchId]);

  const fetchSubGroups = useCallback(async () => {
    if (!branchId) return;
    try {
      const res = await api.get(`groups-types/get-all-sub-group?branchId=${branchId}`);
      if (res.data.success) setAllSubGroups(res.data.allData || []);
    } catch {
      toast.error("Failed to fetch subgroups");
    }
  }, [branchId]);

  useEffect(() => {
    if (branchId) {
      fetchLedgerData();
      fetchPaymentAccount();
      fetchGroups();
      fetchSubGroups();
    }
  }, [branchId, fetchLedgerData, fetchPaymentAccount, fetchGroups, fetchSubGroups]);

  /* ---------------------------- TABLE DATA ---------------------------- */

const fetchRealData = useCallback(async (pageNum = 1, reset = false) => {
  if (!branchId || !endDate) return;

  try {
    if (pageNum === 1) setPageLoading(true);
    else setScrollLoading(true);

    const res = await api.get(
      `receipt/get-receipt-entry?branchId=${branchId}&date=${endDate}&page=${pageNum}&limit=${LIMIT}&order=desc`
    );

    if (res.data.success) {
      const newData = res.data.entries || [];

      setTotalRecoard(res.data.total || 0);
      setGroupedData(prev => reset ? newData : [...prev, ...newData]);
      setHasMore(newData.length === LIMIT);
    }
  } catch (error) {
    toast.error("Failed to load receipt data.");
  } finally {
    setPageLoading(false);
    setScrollLoading(false);
  }
}, [branchId, endDate]);


  const fetchFinalData = useCallback(async () => {
    if (!branchId) return;
    try {
      const res = await api.get(`receipt/get-all-receipt?branchId=${branchId}`);
      if (res.data.success) setFinalData(res.data.entries || []);
    } catch (error) {
      toast.error("Failed to load receipt data.");
    }
  }, [branchId]);

  useEffect(() => {
    if (branchId && endDate) {
      setPage(1);
      setHasMore(true);
      fetchRealData(1, true);
    }
  }, [branchId, endDate, fetchRealData]);

  useEffect(() => {
    if (branchId) fetchFinalData();
  }, [branchId, fetchFinalData]);

useEffect(() => {
  if (page > 1) {
    fetchRealData(page);
  }
}, [page]);


  const refreshTable = () => {
    setPage(1);
    setHasMore(true);
    fetchRealData(1, true);
  };

  /* ---------------------------- COLUMNS ---------------------------- */

  const ReceiptColumns = useMemo(() => ([
    { header: t('name'), placeholder: 'name', inputType: 'input', active: true, filteration: true, combined: true ,type: 'text',uniqueId:"#name"},
    { header: t('address'), placeholder: 'address', inputType: 'input', active: true, combined: true,type: 'text',uniqueId:"#address" },
    { header: t('phone'), placeholder: 'phone', inputType: 'input', active: true, combined: true,type: 'text',uniqueId:"#phone" },
   { header: t('description'), placeholder: 'description', inputType: 'input', active: true, type: 'text',uniqueId:"#description" },
   { header: t('paymentType'), placeholder: 'paymentType', inputType: 'dropdown', options: [], active: true, addDropDownOptions: true,type: 'text',uniqueId:"#paymentType" },
   { header: t('amount'), placeholder: 'amount', inputType: 'input', active: true,type: 'number' ,uniqueId:"#account"},
   isEditMode && { header: t('reason'), placeholder: 'reason', inputType: 'input', active: true, type: 'text',uniqueId:"#reason" },
  ]), [isEditMode, t]);

  const visibleReceiptColumns = ReceiptColumns.filter(c => c.active && c.status !== 'auto');

  const ledgerGroupMappingSales = {
    customerName: "",
    typeOfBricks: "",
    paymentType: "Assets",
  };

  const addAutoFields = (data, columns) => {
    const autoCols = columns.filter(col => col.status === 'auto');
    return data.map(row => {
      const updated = { ...row };
      autoCols.forEach(col => {
        updated[col.placeholder] = col.defaultValue;
      });
      return updated;
    });
  };

  const allSavedReceiptRows = useMemo(() =>
    finalData.flatMap(entry => entry.receipt || []),
    [finalData]
  );

  /* ---------------------------- ACTIONS ---------------------------- */

  const handleSave = async () => {
    const receiptKey = isEditMode ? editingEntryKey : uuidv4();
    setSaving(true);

    try {
      const receiptWithAuto = addAutoFields(receiptData, ReceiptColumns).map(item => ({
        ...item,
        payment_key: receiptKey,
        branchId
      }));

      if (isEditMode) {
        const payload = {
          entryDate: updatedDate || endDate,
          receiptKey,
          receipt: receiptWithAuto,
          branchId,
          adminId,
        };

        const updateRes = await api.post(`receipt/update-receipt-entry`, payload);

        if (updateRes.data.success) {
          fetchFinalData();
          refreshTable();

          if (isModal && onModalSuccess) onModalSuccess();
          else if (comeBack) navigate(comeBack, { state: { entry: updateRes.data.data?.receipt?.[0] } });

          toast.success("Data updated successfully!");
        }
      } else {
        const payload = {
          entryDate: endDate,
          receiptKey,
          receipt: receiptWithAuto,
          branchId
        };

        const createRes = await api.post(`receipt/create-receipt-entry`, payload);

        if (createRes.data.success) {
          toast.success('Data saved successfully!');
          fetchFinalData();
          refreshTable();

          if (isModal && onModalSuccess) onModalSuccess();
          else if (comeBack) navigate(comeBack, { state: { entry } });
        }
      }

      if (isEditMode) {
        await handleCancelEdit();
      } else {
        setResetKey(prev => prev + 1);
      }

      setTimeout(() => {
        const firstInput = document.querySelector('input[type="text"]');
        if (firstInput) firstInput.focus();
      }, 100);

    } catch (error) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to save data.";

      toast.error(backendMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setIsEditMode(true);
    setEditingEntryKey(entry.receiptKey);
    setReceiptData(entry.receipt || []);
    setResetKey(prev => prev + 1);
  };

  const handleCancelEdit = () => {
    return new Promise(resolve => {
      setEditingEntry(null);
      setIsEditMode(false);
      setEditingEntryKey(null);
      setReceiptData([]);
      setResetKey(prev => prev + 1);

      if (isModal && onModalClose) onModalClose();
      else if (comeBack) navigate(comeBack, { state: { entry } });

      setTimeout(() => {
        const receiptSection = document.getElementById('receipt-section');
        const firstInput = receiptSection?.querySelector('input[type="text"]');
        if (firstInput) firstInput.focus();
        resolve();
      }, 0);
    });
  };

  const handleDeleteReceiptApi = async (entry) => {
    try {
      const res = await api.post("receipt/delete-receipt-entry", {
        receiptKey: entry.receiptKey,
        branchId,
        adminId,
        role: userRole,
        deleteReason: entry.deleteReason,
        deletePassword: entry.deletePassword,
        entry,
      });

      if (res.data.success) {
        toast.success("Receipt entry deleted successfully");
        setShowDeleteModal(false);
        setSelectedReceipt(null);
        refreshTable();
        fetchFinalData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Delete failed");
    }
  };

  /* ---------------------------- SCROLL ---------------------------- */

const handleScroll = (e) => {
  const { scrollTop, scrollHeight, clientHeight } = e.target;

  if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !scrollLoading) {
    setPage(prev => prev + 1);
  }
};


  /* ---------------------------- RENDER ---------------------------- */

  return (
    <div className="text-center">

      {/* ---------------- INPUT TABLE ---------------- */}
      <div className="w-full">
        <div className="pt-2">
          <TableComponent
            columns={visibleReceiptColumns}
            onUpdateData={fetchLedgerData}
            allLedgers={allLedgers}
            allPaymentData={allPaymentData}
            updatePaymentAccount={fetchPaymentAccount}
            setAllLedgers={setAllLedgers}
            ledgerData={ledgerData}
            setLedgerData={setLedgerData}
            allGroups={allGroups}
            allSubGroups={allSubGroups}
            setAllGroups={setAllGroups}
            setAllSubGroups={setAllSubGroups}
            heading={t("receipt")}
            tableUniqueId='#receipt_table'
            uniqueForFilter="reportsData"
            onDataChange={setReceiptData}
            resetTrigger={resetKey}
            allowMultiple={false}
            suggestionsData={allSavedReceiptRows}
            firstInputPlaceholder="name"
            finalData={finalData}
            calculateTotal={false}
            changeDesignHeading={true}
            field="name"
            defaultLedgerGroup=""
            columnLedgerGroups={ledgerGroupMappingSales}
            tabIndexStart={5}
            createDyanamicAccount={false}
            onLastInputEnter={() => {
              setTimeout(() => {
                const saveButton = document.getElementById('receipt-save-btn');
                if (saveButton) saveButton.focus();
              }, 100);
            }}
            {...(editingEntry ? { initialData: editingEntry.receipts } : {})}
          />
        </div>

        {/* ---------------- ACTION BUTTONS ---------------- */}
        <div className="flex justify-end gap-2 mt-2">
          <CancelButton onClick={handleCancelEdit} text={t('cancel')} tabIndex={-1} />
          <SaveButton
            className="receipt-save-button"
            onClick={(e) => {
              if (e.type === 'click' || (e.type === 'keydown' && e.key === 'Enter')) {
                handleSave();
              }
            }}
            text={saving ? t('processing') : t('save')}
            disabled={saving}
            tabIndex={1}
          />
        </div>

        {/* ---------------- MAIN LOADER ---------------- */}
        {pageLoading && (
          <div className="flex justify-center items-center w-full py-10">
            <LoadingSpinner size={70} color="#be8024" />
          </div>
        )}

        {/* ---------------- RECEIPT TABLE ---------------- */}
        {!pageLoading && !isModal && !onModalClose && (
          groupedData.length > 0 ? (
            <div>
              <div
                className="overflow-x-auto lg:overflow-visible mt-2 max-h-[70vh] overflow-y-auto relative custom-scrollbar"
                onScroll={handleScroll}
              >
                <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
                  <thead className="sticky top-0 z-20 bg-theme-headerBg">
                    <tr className="bg-theme-headerBg text-theme-color">
                      <th colSpan={6} className="text-center py-[2px] font-medium text-sm">
                        {t('receipt')}
                      </th>
                    </tr>
                    <tr className="text-theme-headerBg bg-theme-tableBg border-theme-headerBg">
                      <th className="border-b border-e px-1 text-center font-medium text-sm">
                        {t('srNumber')}
                      </th>
                      <th className="border-b px-1 py-[2px] text-start font-medium text-sm">
                        {t('receiptInfo')}
                      </th>
                      <th className="border-b px-1 text-start font-medium text-sm">
                        {t('description')}
                      </th>
                      <th className="border-b px-1 text-center font-medium text-sm">
                        {t('paymentType')}
                      </th>
                      <th className="border-b border-e px-1 text-center font-medium text-sm">
                        {t('amount')}
                      </th>
                      <th className="border-b px-1 text-center font-medium text-sm">
                        {t('actions')}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {groupedData.map((entry, idx) => (
                      <tr key={entry.receiptKey} className="bg-theme-tableBg">
                        <td className="border-b border-e text-center font-bold text-sm">
                          {totalRecoard - idx}
                        </td>

                        <td className="border-b capitalize break-words text-sm cursor-pointer">
                          {entry.receipts.map((row, i) => (
                            <div
                              key={i}
                              onClick={() => {
                                navigate('/account-books/ledger-details', {
                                  state: {
                                    receiptRegister: entry.receipts[0],
                                    comeBack: "/dashboard/receipt-transactions"
                                  }
                                })
                              }}
                            >
                              <div className="flex gap-2 justify-center w-44 capitalize fw-medium">
                                <ContactMarquee firstName={row.name} secondName={row.cousinName} />
                              </div>
                              <div className="flex gap-2 justify-center w-44 capitalize ">
                                <ContactMarquee firstName={row.ledgerSubGroup} secondName={row.subGroupNameInHindi} />
                              </div>
                              <div className="w-44 capitalize">
                                <ContactMarquee firstName={row.phone} secondName={row.address} />
                              </div>
                                <div className="flex my-[2px] px-[2px]">
                                    <span className={`text-xs font-semibold px-2 py-[1px] rounded bg-theme-tableHoverColor text-theme-sidebarBg`}>
                                    Bal: ₹ {Number(row.balance.closing_balance).toLocaleString('en-IN')} {row.balance.closing_balance !== 0 ? `(${row.balance.closing_balance_type})` :""} 
                                    </span>
                                </div>
                              {i < entry.receipts.length - 1 && <hr />}
                            </div>
                          ))}
                        </td>

                        <td className="border-b text-start px-1 text-sm">
                          {entry.receipts.map((row, i) => (
                            <div key={i}>
                              {row.description}
                              {i < entry.receipts.length - 1 && <hr />}
                            </div>
                          ))}
                        </td>

                        <td className="border-b text-center text-sm">
                          {entry.receipts.map((row, i) => (
                            <div key={i}>
                              {row.paymentType ? row.paymentType.name : '-'}
                              {i < entry.receipts.length - 1 && <hr />}
                            </div>
                          ))}
                        </td>

                        <td className="border-b border-e text-center text-sm">
                          {entry.receipts.map((row, i) => (
                            <div key={i}>
                              {Number(row.amount).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                              {i < entry.receipts.length - 1 && <hr />}
                            </div>
                          ))}
                        </td>

                        <td className="border-b text-center align-middle">
                          <div className="flex justify-center gap-2">
                            <FaEdit
                              className="text-gray-700 mt-1 cursor-pointer hover:text-theme-btnBg transition-colors"
                              onClick={async () => {
                                await handleCancelEdit();
                                handleEditEntry(entry);
                              }}
                              title={t("editEntry")}
                            />
                            <RiDeleteBin6Fill
                              className="text-red-700 mt-1 cursor-pointer hover:text-red-800 transition-colors"
                              title={t("deleteEntry")}
                              onClick={() => {
                                setSelectedReceipt(entry);
                                setShowDeleteModal(true);
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {scrollLoading && (
                  <div className="py-2 text-center text-sm text-gray-500">
                    Loading more...
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className='text-sm text-gray-600'>{t('noDataAvailable')}</p>
          )
        )}
      </div>

      {/* ---------------- DELETE MODAL ---------------- */}
      {showDeleteModal && (
        <DeleteDataModal
          selectedLedger={selectedReceipt}
          handleDeleteModalClose={() => {
            setShowDeleteModal(false);
            setSelectedReceipt(null);
          }}
          handleDeleteLedgerApi={handleDeleteReceiptApi}
          showinputFields={true}
        />
      )}
    </div>
  );
}